import { ProposalApi } from "@/apis/v2/ProposalApi";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const QUERY_KEY = 'Proposals';
const PAGE_SIZE = 20;

function handleError(error) {
    if (!error?.response) {
        customToast.error('서버와의 연결이 원활하지 않습니다.');
        return;
    }

    customToast.error(error.response?.data?.message);
}

// 새 제안이 계속 들어오므로 탭을 열 때마다 다시 불러온다 (기본 staleTime: Infinity 무시)
export function useProposalsQuery({ userPageId }) {
    return useInfiniteQuery({
        queryKey: [QUERY_KEY, userPageId],
        enabled: !!userPageId,
        staleTime: 0,
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const response = await ProposalApi.getProposals({ params: { userPageId, offset: pageParam, limit: PAGE_SIZE } });

            return response?.data?.data;
        },
        getNextPageParam: (lastPage) => {
            const nextOffset = lastPage.offset + lastPage.items.length;
            return nextOffset < lastPage.total ? nextOffset : undefined;
        },
    });
}

function useProposalMutation(mutationFn) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (variables) => {
            const response = await mutationFn(variables);

            return response?.data?.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: handleError,
    });
}

export function useUpdateProposalMutation() {
    return useProposalMutation(({ id, body }) => ProposalApi.updateProposal({ id, body }));
}

export function useDeleteProposalMutation() {
    return useProposalMutation(({ id }) => ProposalApi.deleteProposal({ id }));
}

// 페이지 정보(proposalConfig)가 바뀌므로 UserPage 쿼리를 다시 불러온다 → UserPageContext 갱신
export function useUpdateProposalSettingsMutation() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            const response = await ProposalApi.updateProposalSettings({ body });

            return response?.data?.data;
        },
        onSuccess: (data, { body }) => {
            customToast.success('enabled' in body ? (data?.enabled ? '제안 받기를 켰어요.' : '제안 받기를 껐어요.') : '알림 설정을 저장했어요.');
            return queryClient.invalidateQueries({ queryKey: ['UserPage'] });
        },
        onError: handleError,
    });
}
